import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Snowflake } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { IMAGES } from "@/lib/content";

const SEASONS = [
  {
    tag: "Winter",
    months: "Jun · Jul · Aug",
    title: "Snow & fireside",
    blurb:
      "Central Otago at its most cinematic — deep snow across Cardrona, Treble Cone, The Remarkables and Coronet Peak, long fireside evenings, warm mulled wine.",
    items: [
      "Skiing & snowboarding",
      "Heli-skiing & backcountry",
      "Snow & mountain sports catering",
      "Après-ski grazing",
      "Fireside recovery dinners",
      "Onsen & hot pools",
    ],
    image: IMAGES.winter,
    accent: "text-gold",
    snowLink: true,
  },
  {
    tag: "Spring",
    months: "Sep · Oct · Nov",
    title: "Bloom & renewal",
    blurb:
      "Cherry blossoms in Wānaka, Cromwell and Arrowtown, lambing in the high country, and trails opening up as the snowline lifts.",
    items: [
      "Cherry-blossom walks",
      "Trout fishing opens",
      "Mountain biking (early)",
      "Wine tastings — new season",
      "Alpine hiking as snow clears",
      "Lake kayaking",
    ],
    image: IMAGES.summer,
    accent: "text-forest-soft",
  },
  {
    tag: "Summer",
    months: "Dec · Jan · Feb",
    title: "Long alpine days",
    blurb:
      "Bright, warm, endlessly long days on Lake Wānaka, Wakatipu and Hawea — swimming, wine country, alfresco dinners at golden hour.",
    items: [
      "Swimming & lake days",
      "Central Otago wine tours (pinot country)",
      "Mountain biking (all trails)",
      "Jet boating",
      "Paragliding & scenic flights",
      "Sunset alfresco dinners",
    ],
    image: IMAGES.landscape,
    accent: "text-metallic-gold",
  },
  {
    tag: "Autumn",
    months: "Mar · Apr · May",
    title: "Golden Central Otago",
    blurb:
      "Arrowtown poplars turn gold, wine harvest across the region, still-warm afternoons under a low honey light. The photographer's season.",
    items: [
      "Autumn colour drives",
      "Wine harvest & cellar door",
      "Otago Central Rail Trail cycling",
      "Trout fishing (prime)",
      "Horse trekking",
      "Bracing alpine hikes",
    ],
    image: IMAGES.adventure,
    accent: "text-oxblood-soft",
  },
];

const Panel = ({ s }) => (
  <div className="relative overflow-hidden group" data-testid={`season-${s.tag.toLowerCase()}`}>
    <div className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden">
      <motion.img
        src={s.image}
        alt={s.title}
        className="h-full w-full object-cover img-cinematic"
        initial={{ scale: 1.12 }}
        whileInView={{ scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
      />
      {/* stronger scrim for readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/30" />
      <div className="absolute inset-0 bg-black/30" />
    </div>

    <div className="absolute inset-0 flex flex-col justify-end p-7 md:p-9">
      <span className={`font-sans text-xs tracking-[0.3em] uppercase ${s.accent} drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]`}>
        {s.tag} &nbsp;·&nbsp; {s.months}
      </span>
      <h3 className="mt-3 font-serif font-light text-ink text-3xl md:text-4xl leading-tight drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
        {s.title}
      </h3>
      <p className="mt-4 font-sans text-[0.95rem] md:text-base text-ink max-w-sm leading-relaxed drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
        {s.blurb}
      </p>
      <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 max-w-sm">
        {s.items.map((it) => (
          <li key={it} className="flex items-center gap-2 font-sans text-xs text-ink/95 drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
            <span className="h-1 w-1 rounded-full bg-gold" />
            {it}
          </li>
        ))}
      </ul>
      {s.snowLink && (
        <Link
          to="/snow"
          data-testid={`season-${s.tag.toLowerCase()}-snow-link`}
          className="mt-6 inline-flex items-center gap-2 font-sans text-[0.7rem] tracking-[0.2em] uppercase text-metallic-gold hover:text-ink transition-colors duration-300 w-max"
        >
          <Snowflake className="h-3.5 w-3.5" strokeWidth={1.6} />
          Live snow & weather report →
        </Link>
      )}
    </div>
  </div>
);

export default function Seasonal() {
  return (
    <section id="seasons" className="relative bg-bg" data-testid="seasonal">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 pt-28 md:pt-40 pb-16">
        <Reveal>
          <Overline>Four Seasons · Central Otago</Overline>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-ink text-4xl md:text-6xl lg:text-7xl leading-[1] max-w-3xl">
            A stay for every <em className="italic text-metallic-gold">season</em>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-6 font-sans text-base md:text-lg text-ink/80 max-w-2xl leading-relaxed">
            From first snow to golden autumn — the year in Wānaka&rsquo;s Lakes District,
            told through what our guests actually do here.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        {SEASONS.map((s) => (
          <Panel key={s.tag} s={s} />
        ))}
      </div>
    </section>
  );
}
